import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatAddress, formatDateTime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { CompanyDialog } from "@/features/companies/CompanyDialog";
import { MergeCompanyDialog } from "@/features/companies/MasterDataReview";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-6 border-b border-line py-2 last:border-0">
    <span className="text-[12px] text-ink-tertiary">{label}</span>
    <span data-testid={testId} className="max-w-[60%] text-right text-[13px] text-ink">
      {value || "—"}
    </span>
  </div>
);

export default function CompanyDetailPage() {
  const { t } = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);   // Fix U

  const { data, isLoading } = useQuery({
    queryKey: ["company", id],
    queryFn: async () => (await api.get(`/companies/${id}`)).data,
  });

  const { data: history } = useQuery({
    queryKey: ["company-history", id],
    queryFn: async () => (await api.get(`/audit/entity/companies/${id}`)).data,
    enabled: can("audit", "read"),
  });

  const company = data?.company;

  return (
    <>
      <PageHeader
        title={company?.nombre || t("app.loading")}
        subtitle={company ? t(`companies.types.${company.tipo}`) : ""}
        testId="company-detail-header"
        breadcrumb={
          <button
            data-testid="company-back"
            onClick={() => navigate("/companias")}
            className="mb-1 flex items-center gap-1 text-[12px] text-ink-tertiary hover:text-brand"
          >
            <ArrowLeft className="h-3 w-3" /> {t("companies.title")}
          </button>
        }
        actions={
          <>
            {company && <StatusChip tone={statusTone(company.status)} label={t(`status.${company.status}`)} />}
            {can("companies", "update") && (
              <Button
                data-testid="company-edit-button"
                onClick={() => setEditOpen(true)}
                variant="outline"
                className="h-8 rounded-[6px] text-[13px]"
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                {t("app.edit")}
              </Button>
            )}
            {can("companies", "delete") && company && !company.fusionada_en && (
              <Button data-testid="company-merge-button" variant="outline" onClick={() => setMergeOpen(true)}
                className="h-8 rounded-[6px] text-[13px]">
                {t("companies.merge_button")}
              </Button>
            )}
          </>
        }
      />
      <MergeCompanyDialog open={mergeOpen} onOpenChange={setMergeOpen} company={company}
        onMerged={(destino) => navigate(`/companias/${destino}`)} />
      {company?.fusionada_en && (
        <p data-testid="company-merged-banner" className="border-b border-line bg-surface-sunken px-6 py-2 text-[12.5px] text-ink">
          {t("companies.merged_into")}{" "}
          <button onClick={() => navigate(`/companias/${company.fusionada_en}`)} className="font-medium text-brand hover:text-brand-hover">
            {company.fusionada_en_nombre}
          </button>
        </p>
      )}

      <div className="flex-1 overflow-y-auto p-6" data-testid="company-detail-page">
        {isLoading ? (
          <Skeleton className="h-[300px] w-full rounded-[8px]" />
        ) : (
          <Tabs defaultValue="data">
            <TabsList className="mb-4 h-9 rounded-[6px] bg-white">
              <TabsTrigger value="data" data-testid="tab-company-data" className="text-[13px]">
                {t("companies.data_tab")}
              </TabsTrigger>
              <TabsTrigger value="buildings" data-testid="tab-company-buildings" className="text-[13px]">
                {t("companies.buildings_tab")} ({data?.properties?.length || 0})
              </TabsTrigger>
              {can("audit", "read") && (
                <TabsTrigger value="activity" data-testid="tab-company-activity" className="text-[13px]">
                  {t("companies.activity_tab")}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="data">
              <div className="grid gap-4 lg:grid-cols-2">
                <section className="rounded-[8px] border border-line bg-white p-4">
                  <h2 className="mb-2 text-[13px] font-semibold text-ink">{t("companies.data_tab")}</h2>
                  <Row label={t("companies.name")} value={company?.nombre} testId="company-name" />
                  <Row label={t("companies.brands")} testId="company-brands"
                    value={(company?.marcas || [company?.brand]).map((b) => (b === "bb" ? "B&B" : "Betos")).join(" · ")} />
                  <Row label={t("companies.type")} value={t(`companies.types.${company?.tipo}`)} />
                  <Row label={t("companies.payment_terms")} value={company?.condiciones_pago_dias} />
                  <Row label={t("companies.buildings")} value={company?.edificios_count} />
                  <Row label={t("companies.open_orders")} value={company?.ordenes_abiertas} />
                  <Row label={t("app.notes")} value={company?.notas} />
                </section>
                <section className="rounded-[8px] border border-line bg-white p-4">
                  <h2 className="mb-2 text-[13px] font-semibold text-ink">{t("companies.main_contact")}</h2>
                  <Row label={t("app.name")} value={company?.contacto?.nombre} />
                  <Row label={t("app.email")} value={company?.contacto?.email} />
                  <Row label={t("app.phone")} value={company?.contacto?.telefono} />
                </section>
              </div>
            </TabsContent>

            <TabsContent value="buildings">
              <div className="overflow-hidden rounded-[8px] border border-line bg-white">
                <table className="w-full text-[13px]" data-testid="company-buildings-table">
                  <thead className="bg-surface-sunken">
                    <tr className="border-b border-line text-[12px] uppercase tracking-[0.04em] text-ink-tertiary">
                      <th className="px-3 py-2 text-left">{t("app.name")}</th>
                      <th className="px-3 py-2 text-left">{t("app.address")}</th>
                      <th className="px-3 py-2 text-right">{t("properties.units_count")}</th>
                      <th className="px-3 py-2 text-right">{t("properties.open_orders")}</th>
                      <th className="px-3 py-2 text-left">{t("app.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.properties || []).map((p) => (
                      <tr
                        key={p.id}
                        data-testid={`company-building-${p.id}`}
                        onClick={() => navigate(`/edificios/${p.id}`)}
                        className="h-[44px] cursor-pointer border-b border-line last:border-0 hover:bg-surface-sunken"
                      >
                        <td className="px-3 font-medium">{p.nombre}</td>
                        <td className="px-3 text-ink-tertiary">{formatAddress(p.direccion)}</td>
                        <td className="px-3 text-right tabular-nums">{p.unidades_count}</td>
                        <td className="px-3 text-right tabular-nums">{p.ordenes_abiertas}</td>
                        <td className="px-3">
                          <StatusChip tone={statusTone(p.status)} label={t(`status.${p.status}`)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="activity">
              <div className="rounded-[8px] border border-line bg-white" data-testid="company-activity">
                {(history?.items || []).map((a) => (
                  <div key={a.id} className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-0">
                    <StatusChip tone="blue" label={t(`audit.actions.${a.accion}`)} />
                    <span className="flex-1 text-[13px] text-ink">
                      {a.diff?.length ? a.diff.map((d) => d.field).join(", ") : a.detalle || "—"}
                    </span>
                    <span className="text-[12px] text-ink-tertiary">{a.user_nombre}</span>
                    <span className="text-[12px] tabular-nums text-ink-tertiary">
                      {formatDateTime(a.timestamp)}
                    </span>
                  </div>
                ))}
                {!history?.items?.length && (
                  <p className="px-4 py-8 text-[13px] text-ink-tertiary">{t("app.empty")}</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <CompanyDialog open={editOpen} onOpenChange={setEditOpen} company={company} />
    </>
  );
}
